import { useMemo } from "react";
import { PolicyType } from "shared";
import { CheckIcon } from "@heroicons/react/20/solid";

import { Button } from "../../../common/Atoms/Button";
import { Modal } from "../../../common/Atoms/Modal";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useWelcomeMessage } from "../../hooks/useWelcomeMessage";
import { useModals } from "../../hooks/useModals";
import { useGroups } from "../../hooks/useGroups";
import { mixpanelService } from "../../../common/lib/mixpanel";

/** Display label per policy type, in the order rows are listed */
const POLICY_LABELS: [PolicyType[], string][] = [
  [[PolicyType.REMINDER], `Email reminders`],
  [[PolicyType.STATEMENT], `Monthly statements`],
  [[PolicyType.SMS], `SMS reminders`],
  [[PolicyType.QUOTE_EMAIL], `Quote reminders`],
  [[PolicyType.LATE_FEE], `Invoice late fees`],
  [[PolicyType.STATEMENT_LATE_FEE], `Customer late fees`],
  [[PolicyType.DISCOUNT], `Prompt payment discounts`],
  [[PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION], `Calls & escalations`],
];

export function WelcomeModal() {
  const currentOrg = useGetSelectedOrganisation();
  const { showWelcomeMessage, dismissWelcomeMessage } = useWelcomeMessage();
  const { show } = useModals();
  const { data: groups } = useGroups();

  const policyRows = useMemo(() => {
    const policies = (groups || []).flatMap(group => group.policies || []);

    return POLICY_LABELS
      .map(([types, label]) => ({
        label,
        count: policies.filter(policy => policy.policy_type && types.includes(policy.policy_type)).length,
      }))
      .filter(row => row.count > 0);
  }, [groups]);

  function onBookDemo() {
    dismissWelcomeMessage();
    show(`call`);
    mixpanelService.demoCallClickeWelcomeModal();
  }

  if (!showWelcomeMessage) {
    return null;
  }

  return (
    <Modal
      open={ showWelcomeMessage }
      setOpen={ dismissWelcomeMessage }
      className={ `p-0 sm:p-0 overflow-hidden` }
    >
      <div className={ `px-8 pt-9 sm:px-11 sm:pt-10` }>
        <div className={ `flex items-center gap-2.5` }>
          <img
            className={ `h-7 w-auto` }
            src={ `/img/logo-color.png` }
            alt={ `` }
          />
          <span className={ `text-xs font-semibold uppercase tracking-[0.1em] text-paidniceGreen-500` }>{ `Setup complete` }</span>
        </div>
        <h2 className={ `mt-4 text-2xl font-bold tracking-tight text-paidniceGreen-900` }>{ `Welcome to Paidnice` }</h2>
        <p className={ `mt-2.5 text-[14.5px] leading-relaxed text-gray-600` }>
          { `You're all set up with some initial policies. Explore each one to customise how and when actions are triggered.` }
        </p>

        { policyRows.length > 0 && (
          <ul className={ `mt-5 divide-y divide-gray-100 rounded-[10px] border border-gray-200` }>
            { policyRows.map(row => (
              <li
                key={ row.label }
                className={ `flex items-center gap-3 px-4 py-3` }
              >
                <span className={ `grid h-5 w-5 shrink-0 place-items-center rounded-full bg-paidniceGreen-50 text-paidniceGreen-600` }>
                  <CheckIcon className={ `h-3.5 w-3.5` }
                    aria-hidden={ true } />
                </span>
                <span className={ `text-sm font-semibold text-paidniceGreen-800` }>{ row.label }</span>
                <span className={ `ml-auto text-[12.5px] text-gray-400` }>
                  { `${row.count} draft ${row.count === 1 ? `policy` : `policies`}` }
                </span>
              </li>
            )) }
          </ul>
        ) }

        <p className={ `mt-4 text-[13.5px] leading-relaxed text-gray-500` }>
          { `Every policy is created as a ` }
          <strong className={ `text-paidniceGreen-800` }>{ `draft` }</strong>
          { ` - nothing is sent or charged automatically until you review and approve it.` }
        </p>
        <p className={ `mt-2.5 text-[13.5px] leading-relaxed text-gray-500` }>
          { `You have ` }
          <strong className={ `text-paidniceGreen-800` }>{ `${currentOrg?.freeActionLimit || 0} free actions` }</strong>
          { ` to trial before choosing a plan. Actions include late fees, emails, statement sends and more.` }
        </p>
      </div>

      <div className={ `mt-6 flex flex-wrap items-center justify-end gap-2.5 border-t border-gray-100 bg-gray-50 px-8 py-5 sm:px-11` }>
        <Button
          color={ `white` }
          size={ `xl` }
          className={ `h-11 max-h-none rounded-lg px-6` }
          onClick={ onBookDemo }
        >
          { `Book a free setup call` }
        </Button>
        <Button
          size={ `xl` }
          className={ `h-11 max-h-none rounded-lg px-6` }
          onClick={ dismissWelcomeMessage }
        >
          { `Review your draft policies` }
        </Button>
      </div>
    </Modal>
  );
}
